/* eslint-disable @typescript-eslint/no-explicit-any */
// Account - loyaltyStaticPage Page -  Auth Page

import { GetServerSideProps } from 'next';
import { inspect } from 'util';
import { ResponsiveGrid } from '@adobe/aem-react-editable-components';
import { ApolloProvider } from '@apollo/client';
import { respGridUtil, getProcessEnvs, logger } from '@marriott/mi-headless-utils';
import { getDeployedEnvType } from '@marriott/mi-apollo-client-utils';
import { constants } from '@marriott/mi-account-components/constants';
import { useMiApolloClient } from '@marriott/mi-apollo-client-utils';
import { prePageCall, Layout } from '@marriott/mi-headless-utils';
import { fetchFlags, isACDLEnabled, preProcessorCall } from '@marriott/mi-account-components/utils';
import { FEATURE_FLAGS, TARGETS } from '@marriott/mi-feature-flag-utils';

import { loyaltyStaticPageModel } from '../../_localDevMocks';
import operationSignatures from '../../_constants/operationSignatures.json';

const { NEXT_PUBLIC_AEM_PATH, SKIP_PREPROCESSOR_CALL, IS_LOCAL_DEV } = getProcessEnvs();
const { ITEM_PATH_ROOT, UNAUTH_URL, NEXT_PUBLIC_ENV_KEYS } = constants;
global.loggerInstance = logger({}) as CallableFunction;
const { log } = global.loggerInstance('DynamicLoyaltyPage');
// final data would have to come from 2 calls:
// 1. Page level call from AEM - that will populate the labels on components
// 2. Functional data for components like list of amenities and other related information
// import jsonContent from '../../mock/label-info.json';

export default function LoyaltyStaticPage({ model, deployedEnvType }: any) {
  const MiApolloClient = useMiApolloClient(operationSignatures, deployedEnvType);

  const {
    metaNames,
    metaProperties,
    title,
    staticComponentClientLibsCSS,
    staticComponentClientLibsJS,
    gridCss,
    authorMode,
    hreflangList,
    canonical,
  } = model;
  const responsiveGridModel = respGridUtil(model?.cqItems?.root?.[':items']?.responsivegrid);
  return (
    <ApolloProvider client={MiApolloClient}>
      <Layout
        metaNames={metaNames}
        metaProps={metaProperties}
        title={title}
        staticComponentClientLibsCSS={staticComponentClientLibsCSS}
        staticComponentClientLibsJS={staticComponentClientLibsJS}
        gridCss={gridCss}
        isAuthorMode={authorMode}
        hreflangList={hreflangList ?? []}
        canonical={canonical}
      >
        <ResponsiveGrid
          {...responsiveGridModel}
          model={responsiveGridModel}
          pagePath={NEXT_PUBLIC_AEM_PATH}
          itemPath={ITEM_PATH_ROOT}
          customClassName="account-page-wrapper"
        />
      </Layout>
    </ApolloProvider>
  );
}

//Any product specific page data apart from common data like session, datalayer
//can be returned from this function and the same is passed as props to the page Component
const loyaltyStaticPageSpecificData = async (request: any) => {
  //any page specific code can be written here.
  const deployedEnvType = getDeployedEnvType();
  const { GOOGLE_MAP_API_KEY } = getProcessEnvs();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const featureFlags = await fetchFlags([FEATURE_FLAGS.EXPERIENCE_MCOM_ACCOUNT_ACDL_ENABLED_PAGES_LOCALES], {
    target: TARGETS.APP.ACCOUNT_RENDERER,
  });

  let response: any = { operationSignatures: [], deployedEnvType, featureFlags, GOOGLE_MAP_API_KEY };
  const resolvedUrlString: string = request?.req?.url;
  const isAuthorMode = resolvedUrlString?.includes('isAuthorMode');
  const urlQueryParameters = new URLSearchParams(request?.query)?.toString();
  const preprocessorPath = urlQueryParameters ? `${UNAUTH_URL}?${urlQueryParameters}` : UNAUTH_URL;
  /**
   * Preprocessor Call Start
   */

  /** Bypassing preprocessor call intentionally for testing. It should be reverted back */
  if (SKIP_PREPROCESSOR_CALL !== 'true' && IS_LOCAL_DEV !== 'true' && !isAuthorMode) {
    // Making page specific preprocessor call
    log.debug(`[DynamicLoyaltyPageSpecificData] session Id: ${request?.req?.cookies?.sessionID}`);

    const preprocessorResponse = await preProcessorCall(
      request?.req?.cookies?.sessionID,
      request?.req,
      preprocessorPath,
      {}
    );
    log.debug(`[DynamicLoyaltyPageSpecificData] PreprocessorResponse: ${inspect(preprocessorResponse)}`);
    if (preprocessorResponse?.data?.error) {
      log.debug(`[DynamicLoyaltyPageSpecificData] page error obj - ${inspect(preprocessorResponse?.data?.redirect)}`);
      const error = {
        redirect: { redirect: preprocessorResponse?.data?.redirect },
        value: true,
      };
      response = {
        operationSignatures: [],
        deployedEnvType,
        error,
        nextPublicEnvKeys: constants?.NEXT_PUBLIC_ENV_KEYS,
        featureFlags,
        GOOGLE_MAP_API_KEY,
      };
    } else if (preprocessorResponse?.data) {
      if (preprocessorResponse?.data?.redirect) {
        const error = {
          redirect: {
            redirect: {
              destination: preprocessorResponse?.data?.nextStateURI,
              permanent: false,
            },
          },
          value: true,
        };
        response = {
          operationSignatures: [],
          deployedEnvType,
          error,
          nextPublicEnvKeys: constants?.NEXT_PUBLIC_ENV_KEYS,
          featureFlags,
          GOOGLE_MAP_API_KEY,
        };
      } else {
        response = {
          operationSignatures: [],
          deployedEnvType,
          userState: preprocessorResponse?.data?.userState,
          nextPublicEnvKeys: constants?.NEXT_PUBLIC_ENV_KEYS,
          featureFlags,
          GOOGLE_MAP_API_KEY,
        };
      }
    }
  }

  return response;
};

const postAEMModelOperations = async (...args: [any, any, any, Record<string, unknown>, Record<string, any>]) => {
  const [context, , , model] = args;
  const [, , , , pageSpecificData] = args;

  const resolvedUrlString: string = context?.['resolvedUrl'] ?? context?.req?.url ?? '';
  const pathOnly = resolvedUrlString.split('?')[0].toLowerCase().replace(/\/$/, '');

  const pageName = pathOnly.endsWith('/loyalty/earn') ? 'earn' : pathOnly.endsWith('/loyalty/redeem') ? 'redeem' : '';

  const IS_CURRENT_LOCALE_AND_PAGE_ENABLED_WITH_ACDL =
    pageName.length > 0
      ? isACDLEnabled(
          model,
          pageSpecificData?.['featureFlags']?.['experience_mcom_account_acdl_enabled_pages_locales'],
          pageName
        )
      : false;

  return {
    ...model,
    IS_CURRENT_LOCALE_AND_PAGE_ENABLED_WITH_ACDL,
  };
};

//Make pre page calls like session, datalayer, fetch model.json, producSpecifc data etc
//at server side and pass the data is passed as props to page component.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const getServerSideProps: GetServerSideProps<any> = prePageCall(
  loyaltyStaticPageSpecificData,
  loyaltyStaticPageModel,
  false,
  false,
  false,
  undefined,
  postAEMModelOperations
);
